import React, { ChangeEvent, useContext, useRef, useState } from 'react';
import { Breakpoints, FileExtension, NotificationType } from 'common/enums';
import { classnames } from 'helpers/utils';
import { sFileMaxDuration } from 'config/constants';
import { formatFileExtensions, isValidFile } from 'helpers/file-utils';
import { FileUploaderErrorMessage } from 'common/file-uploader-error-message';
import { useMediaQuery } from 'hooks/use-media-query';
import { AppContext, appActions } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { ShareIcon } from 'assets/icons';
import styles from './file-uploader-video.module.scss';

type FileUploaderVideoProps = {
  supportedExtensions?: Array<FileExtension>;
  maxFileSizeInMb?: number;
  notifyNewFile: (file: File) => void;
  canUploadMultipleFiles: boolean;
  browseFilesRef?: React.RefObject<HTMLDivElement | null>;
  isEmptyState?: boolean;
};

const FileUploaderVideo: React.FC<FileUploaderVideoProps> = ({
  supportedExtensions = [],
  maxFileSizeInMb = 0,
  notifyNewFile,
  canUploadMultipleFiles,
  browseFilesRef,
  isEmptyState = false,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>(
    '',
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const { dispatch } = useContext(AppContext);

  const uploadErrors = {
    generic: 'Oh no, something went wrong.',
    size: `File is too big. Maximum file size accepted is ${maxFileSizeInMb}MB.`,
    format: `File format not supported. Please upload only ${formatFileExtensions(
      supportedExtensions,
    ).toUpperCase()} files.`,
    duration: `File is too long. Maximum file length is ${
      sFileMaxDuration / 60
    } minutes.`,
  };

  const verifyMaxQuestionLimit = () => true;

  const verifyAndNotifyFiles = async (files: File[]) => {
    const firstFile = files[0];
    if (canUploadMultipleFiles) {
      files.forEach(async (file) => {
        const maxQuestionsLimitReached = verifyMaxQuestionLimit();
        if (!maxQuestionsLimitReached) return;
        isValidFile(
          file!,
          uploadErrors,
          supportedExtensions,
          maxFileSizeInMb,
        ).then((error) => {
          if (error) {
            dispatch({
              type: appActions.NOTIFICATION,
              notification: new NotificationObject({
                show: true,
                title: `${file.name}`,
                message: `${error.toString()}`,
                type: NotificationType.Error,
              }),
            });
          } else {
            setErrorMessage('');
            notifyNewFile(file);
          }
        });
      });
    } else {
      const error = await isValidFile(
        firstFile!,
        uploadErrors,
        supportedExtensions,
        maxFileSizeInMb,
      );

      if (!error) {
        notifyNewFile(firstFile);
      } else {
        setErrorMessage(error);
      }
    }
  };

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    const arrayFiles = Array.from(files);
    await verifyAndNotifyFiles(arrayFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files ?? []);
    if (droppedFiles.length) await verifyAndNotifyFiles(droppedFiles);
  };

  const dropzone = () => (
    <>
      <div
        role="button"
        tabIndex={0}
        className={classnames(styles.dropzone, {
          [styles.dropzoneActive]: isDragActive,
        })}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click();
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className={styles.dropIcon}>
          <ShareIcon />
        </span>
        <span className={styles.dropTitle}>Drag &amp; drop files here</span>
        <span className={styles.dropHint}>
          or <span className={styles.dropBrowse}>browse</span> to upload
        </span>
        {supportedExtensions.length > 0 && (
          <span className={styles.dropFormats}>
            {formatFileExtensions(supportedExtensions).toUpperCase()}
          </span>
        )}
      </div>
      <input
        type="file"
        ref={fileRef}
        accept={formatFileExtensions(supportedExtensions)}
        onChange={handleInputChange}
        hidden
        multiple={canUploadMultipleFiles}
      />
    </>
  );

  const showBrowseFiles = () => (
    <>
      <button
        type="button"
        key={uploadErrors.format}
        onClick={() => fileRef.current?.click()}
        className={classnames(styles.menuItem, styles.uploadColor)}
      >
        <ShareIcon className={classnames(styles.icon, styles.upload)} />
        <span>Upload</span>
      </button>
      <input
        type="file"
        ref={fileRef}
        accept={formatFileExtensions(supportedExtensions)}
        onChange={handleInputChange}
        hidden
        multiple={canUploadMultipleFiles}
      />
    </>
  );

  const showUploader = () => {
    if (errorMessage) {
      return (
        <FileUploaderErrorMessage
          errorMessage={errorMessage}
          actions={showBrowseFiles()}
        />
      );
    }

    if (isEmptyState) return dropzone();

    return (
      <>
        <button
          type="button"
          key={uploadErrors.format}
          onClick={() => fileRef.current?.click()}
          className={classnames(
            { [styles.menuItem]: !isEmptyState },
            styles.uploadColor,
            { [styles.emptyStateUpload]: isEmptyState },
          )}
        >
          <ShareIcon
            className={classnames({
              [(styles.upload, styles.icon)]: !isEmptyState,
            })}
          />
          <span>Upload</span>
        </button>
        <input
          type="file"
          ref={fileRef}
          accept={formatFileExtensions(supportedExtensions)}
          onChange={handleInputChange}
          hidden
          multiple={canUploadMultipleFiles}
        />
      </>
    );
  };

  if (isMobile) {
    const button = (
      <button
        type="button"
        key={uploadErrors.format}
        onClick={() => fileRef.current?.click()}
        className={classnames(
          styles.uploadColor,
          isEmptyState ? styles.emptyStateUpload : styles.menuItem,
        )}
      >
        <ShareIcon
          className={classnames({
            [styles.icon]: !isEmptyState,
            [styles.upload]: !isEmptyState,
          })}
        />
        <span>Upload</span>
      </button>
    );
    return (
      <div ref={browseFilesRef} id="browseFiles-mobile">
        {button}
        <input
          type="file"
          ref={fileRef}
          accept={formatFileExtensions(supportedExtensions)}
          onChange={handleInputChange}
          hidden
          multiple={canUploadMultipleFiles}
        />
      </div>
    );
  }

  return showUploader();
};

export { FileUploaderVideo };
